/**
 * FP3-029B3 — Calm banner at the top of any provider workspace
 * surface when an admin entered read-only mode via
 * `?as_provider={providerId}`.
 *
 * FP3-029B6B — Now also covers supervising-primary read-only
 * shadowing of their assistants (e.g. Sarah opening Taylor's
 * caseload as a supervision view). The banner copy adapts:
 *   - Admin family: "Admin View · Read-only provider workspace"
 *   - Supervising primary: "Supervision View · Read-only" with
 *     the assistant's name
 *
 * Mounted inside AppLayout so every provider page (Today, Caseload,
 * Schedule, Reports, etc.) shows the banner automatically.
 *
 * The banner does NOT enforce read-only at the data layer — the
 * underlying provider endpoints already block non-provider writes
 * via their own auth checks. The banner is a calm UX layer that
 * (a) tells the user where they are, and (b) provides a clean
 * "Exit to Admin" path back.
 *
 * FP3-035F correction (Issue 3) — Banner redesigned with a larger
 * amber/gold gradient treatment, a solid icon chip, a 2-line copy
 * block ("Viewing {Provider}'s workspace — Read Only" + "You are
 * viewing as an admin. Actions are disabled."), and a solid amber
 * pill for the Exit to Admin button so admin oversight mode is
 * immediately readable on every provider surface.
 */
import { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Eye, X, UsersThree } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function ReadOnlyProviderBanner() {
  const { user, ownProvider } = useAuth();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const asProvider = searchParams.get("as_provider") || "";
  const [provider, setProvider] = useState(null);

  const hasAdminAccess = ["admin", "org_admin", "super_admin"].includes(
    user?.permission_role || ""
  );

  // FP3-029B6B — Supervising-primary path. We don't try to confirm
  // the relationship on the FE; the actual data endpoints are
  // role-gated. The banner just renders any time a non-admin opens
  // ?as_provider= so the user understands they're in a shadow view.
  const isShadow = !!asProvider && asProvider !== ownProvider?.id;

  useEffect(() => {
    if (!asProvider || !isShadow) {
      setProvider(null);
      return;
    }
    if (hasAdminAccess) {
      api
        .getAdminProviderDetail(asProvider)
        .then((r) => setProvider(r.data))
        .catch(() => setProvider(null));
    } else {
      api
        .getProviders()
        .then((r) => {
          const p = (r.data || []).find((x) => x.id === asProvider);
          setProvider(p || null);
        })
        .catch(() => setProvider(null));
    }
  }, [asProvider, hasAdminAccess, isShadow]);

  if (!asProvider || !isShadow || !provider) return null;

  // Strip ?as_provider= from the current path so the Exit button
  // returns the user to the same surface without shadow mode.
  const cleanParams = new URLSearchParams(location.search);
  cleanParams.delete("as_provider");
  const cleanPath = `${location.pathname}${cleanParams.toString() ? `?${cleanParams}` : ""}`;

  const isAdmin = hasAdminAccess;
  const providerName = `${provider.first_name} ${provider.last_name}`.trim() || "Provider";
  // FP3-044B Issue 5 (low-risk) — Surface provider role + school in
  // the banner so the admin always knows who they're previewing and
  // where, without us building a new full-screen shell.
  const providerRole = (provider.role || provider.provider_role_type || "").trim();
  const providerSchool = (
    (provider.school_name || provider.school || provider.campus || "") || ""
  ).trim();
  // FP3-035F correction (Issue 3) — Banner copy made explicit per
  // user request. Admin path emphasizes "Read Only" + the disabled-
  // actions explainer. FP3-069 follow-up #12 — Supervision path now
  // calls out the assistant by name AND tells the supervisor their
  // own workspace is paused, matching the user-requested copy.
  const heading = isAdmin
    ? `Viewing ${providerName}'s workspace — Read Only`
    : `Read-only supervision view — ${providerName}`;
  const subline = isAdmin
    ? "You are viewing as an admin. Provider actions are disabled."
    : `Viewing ${providerName}'s schedule. Your workspace is paused until you exit.`;
  const Icon = isAdmin ? Eye : UsersThree;
  const contextChips = isAdmin
    ? [providerRole, providerSchool].filter((x) => x)
    : [];

  return (
    <div
      data-testid="readonly-provider-banner"
      // FP3-035F correction (Issue 3) — Larger, more distinctive
      // amber/gold treatment so admins always know they're in
      // oversight mode. Not red — this is an oversight indicator,
      // not an error. 4-color stripe (gradient + left accent bar +
      // border + icon chip) makes it immediately readable without
      // being alarming.
      className="relative bg-gradient-to-r from-[#FDF3D8] via-[#FBEBC0] to-[#F8E2AE] border-y-2 border-[#C8951F] px-5 py-3.5 flex items-center justify-between gap-4 shadow-sm"
    >
      {/* Left accent bar — solid amber for stronger affordance */}
      <span
        aria-hidden="true"
        className="absolute left-0 top-0 bottom-0 w-1.5 bg-[#9C6B2E]"
      />
      <div className="flex items-center gap-3 min-w-0 pl-3">
        <span
          aria-hidden="true"
          className="shrink-0 w-9 h-9 rounded-full bg-[#9C6B2E] flex items-center justify-center"
        >
          <Icon size={18} weight="duotone" className="text-white" />
        </span>
        <div className="min-w-0">
          <p
            data-testid="readonly-provider-banner-heading"
            className="text-[14px] font-semibold text-[#5A4A1A] leading-snug heading-font truncate"
          >
            {heading}
          </p>
          <p
            data-testid="readonly-provider-banner-subline"
            className="text-[12px] text-[#7A6A2C] leading-snug mt-0.5"
          >
            {subline}
          </p>
          {contextChips.length > 0 && (
            <div
              data-testid="readonly-provider-banner-context"
              className="flex items-center gap-1.5 mt-1.5 flex-wrap"
            >
              {contextChips.map((chip) => (
                <span
                  key={chip}
                  className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#9C6B2E]/15 text-[#5A4A1A] font-medium"
                >
                  {chip}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      {/* FP3-044 Phase A — When the admin entered the read-only
          provider workspace from the Admin Student Overview (we
          stamp `?from_admin_overview=1` on the Link), Exit returns
          to that student's overview instead of the generic provider
          admin page. Pulls the studentId from the current path. */}
      <Link
        data-testid="readonly-provider-exit"
        to={
          isAdmin
            ? (searchParams.get("from_admin_overview") === "1"
                ? (() => {
                    const m = location.pathname.match(/\/students\/([^/?#]+)/);
                    return m && m[1] ? `/admin/students/${m[1]}` : `/admin/providers/${asProvider}`;
                  })()
                : `/admin/providers/${asProvider}`)
            : "/caseload"
        }
        // FP3-035F correction (Issue 3) — Promoted from underlined
        // link to a solid amber button for stronger affordance.
        className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-[#9C6B2E] hover:bg-[#7A521F] transition-colors shadow-sm"
      >
        <X size={14} weight="bold" />{" "}
        {isAdmin
          ? (searchParams.get("from_admin_overview") === "1"
              ? "Exit to Admin Overview"
              : "Exit to Admin")
          : "Exit read-only"}
      </Link>
    </div>
  );
}

/**
 * useReadOnlyAsProvider — small hook for provider workspace pages
 * that need to disable a mutating button. Returns:
 *   { active, providerId, providerName, isAdmin }
 * Active is true any time the URL carries ?as_provider= AND the
 * resolved provider differs from the caller's own provider id
 * (admin family OR supervising primary).
 */
export function useReadOnlyAsProvider() {
  const { user, ownProvider } = useAuth();
  const [searchParams] = useSearchParams();
  const asProvider = searchParams.get("as_provider") || "";
  const hasAdminAccess = ["admin", "org_admin", "super_admin"].includes(
    user?.permission_role || ""
  );
  const isShadow = !!asProvider && asProvider !== ownProvider?.id;
  // FP3-069 follow-up #12 — Distinguish admin-shadow vs supervision-
  // shadow. The two paths share the same `?as_provider=` URL shape
  // but the read-only enforcement differs: admin shadow should hide
  // the supervisor's PRIVATE pages (Messages, Notifications — the
  // FP3-035F guard), but supervision shadow keeps Messages
  // reachable because the supervisor is opening THEIR OWN inbox to
  // DM the assistant, not peeking at someone else's inbox. The new
  // `mode` field lets consumers (e.g. ProviderMessages.jsx) gate
  // their hard redirects on the precise shadow flavor.
  const mode = isShadow
    ? (hasAdminAccess ? "admin" : "supervision")
    : "off";
  return {
    active: isShadow,
    mode,
    providerId: asProvider,
    isAdmin: hasAdminAccess,
  };
}


/**
 * FP3-043 Phase A follow-up #9 — Student workspace read-only hook.
 *
 * Returns the same shape as `useReadOnlyAsProvider`, but `active`
 * is true under EITHER of:
 *   (a) the existing admin/supervision shadow path (URL carries
 *       `?as_provider=…` and the resolved provider differs from
 *       the caller's own provider), OR
 *   (b) the caller is an admin-family user (admin / org_admin /
 *       super_admin) WITHOUT a shadow — i.e. they opened the
 *       student URL directly from Admin → Students. Admin family
 *       users are never the assigned provider, so any provider-
 *       level mutation surface should disable for them.
 *
 * Reason: backend mutation endpoints already gate writes — this
 * hook just makes the UX match (hide / disable provider actions,
 * show the calm "Admin View" banner).
 */
export function useStudentWorkspaceReadOnly() {
  const { user, ownProvider } = useAuth();
  const [searchParams] = useSearchParams();
  const asProvider = searchParams.get("as_provider") || "";
  const isAdminFamily = ["admin", "org_admin", "super_admin"].includes(
    user?.permission_role || ""
  );
  const isShadow = !!asProvider && asProvider !== ownProvider?.id;
  // Admin family without shadow → still read-only on the student
  // workspace itself (Admin Oversight path).
  const adminOversight = isAdminFamily && !asProvider;
  return {
    active: isShadow || adminOversight,
    mode: isShadow ? "shadow" : adminOversight ? "admin_oversight" : "off",
    providerId: asProvider,
    isAdmin: isAdminFamily,
  };
}
